import '../App.css';

import axios from 'axios';
import { useNavigate } from 'react-router';
import { Camera, Heart, TriangleAlert } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { API_BASE_URL } from '@/constants';
import { NbButton, NbCard, NbChip } from '@/components/Neobrut';
import { HomeMapPreview } from '@/components/Map/HomeMapPreview';

const TICKER_ITEMS = [
  '▲ nueva publicación cada pocas horas',
  '●',
  'pistas reales, vecinos reales',
  '●',
  'publicar es gratis y sin planes pagos',
  '●',
  'red vecinal argentina',
  '●',
];

export const Home = () => {
  const navigate = useNavigate();

  const { data: solvedCases } = useQuery({
    queryKey: ['solvedCases'],
    queryFn: async (): Promise<Record<'solved_cases_count', number>> => {
      const response = await axios.get(`${API_BASE_URL}/pets/solved-cases-count/`);
      return response.data;
    },
  });

  return (
    <>
      {/* Urgent ticker — full-bleed (sibling of centered container) */}
      <div
        className="fill-urgent relative overflow-hidden"
        style={{ borderTop: '3px solid var(--nb-ink)', borderBottom: '3px solid var(--nb-ink)' }}
      >
        <div
          className="nb-ticker-track py-1.5 text-[11px] font-bold uppercase tracking-[0.06em]"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {Array(2)
            .fill(0)
            .map((_, j) => (
              <div key={j} className="flex gap-6">
                {TICKER_ITEMS.map((t, i) => (
                  <span key={`${j}-${i}`}>{t}</span>
                ))}
              </div>
            ))}
        </div>
      </div>

      {/* Centered content — responsive max-width */}
      <div className="mx-auto w-full max-w-md lg:max-w-[1280px] lg:px-6">
        <div className="lg:grid lg:grid-cols-2 lg:items-stretch lg:gap-4">
          {/* Hero */}
          <section className="px-4 pt-5 lg:flex lg:flex-col">
            <h1 className="font-display text-[44px] leading-[0.95] lg:text-[56px]">
              Tu mascota
              <br />
              <span className="nb-highlight">te espera.</span>
            </h1>
            <p className="mt-3 max-w-[320px] text-[14px] font-medium leading-[1.35] lg:max-w-none lg:text-[16px]">
              Red vecinal para reencontrar mascotas perdidas en <strong>Argentina</strong>. Sin
              logins obligatorios. Sin planes pagos. Solo nosotros.
            </p>

            <div className="mt-5 grid grid-cols-2 gap-2.5 lg:mt-auto">
              <NbButton
                fill="lost"
                className="min-w-0 whitespace-normal px-2 py-3 text-center text-[13px] leading-tight"
                onClick={() => navigate('/publish')}
              >
                <TriangleAlert size={16} className="shrink-0" />
                <span>Perdí a mi mascota</span>
              </NbButton>
              <NbButton
                fill="solved"
                className="min-w-0 whitespace-normal px-2 py-3 text-center text-[13px] leading-tight"
                onClick={() => navigate('/publish')}
              >
                <Heart size={16} className="shrink-0" />
                <span>Encontré una</span>
              </NbButton>
            </div>
            <div
              className="mt-2.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em]"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              <span className="nb-blink">●</span>
              Publicar es gratis y tarda menos de 2 min
            </div>
          </section>

          {/* Map block */}
          <section className="px-4 pt-5">
            <div className="mb-2.5 flex items-end justify-between">
              <div>
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  / mapa
                </div>
                <div className="font-display text-[22px]">Cerca tuyo</div>
              </div>
              <NbChip fill="lost" className="cursor-pointer" onClick={() => navigate('/map')}>
                ver todo →
              </NbChip>
            </div>

            <NbCard className="p-0">
              <div
                className="overflow-hidden"
                style={{ borderBottom: '3px solid var(--nb-ink)' }}
              >
                <HomeMapPreview />
              </div>
              <div
                className="flex text-[11px] font-bold uppercase"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                <div
                  className="flex flex-1 items-center gap-1.5 px-2.5 py-2"
                  style={{ borderRight: '2px solid var(--nb-ink)' }}
                >
                  <span
                    className="inline-block h-2.5 w-2.5"
                    style={{ background: 'var(--nb-lost)', border: '1.5px solid var(--nb-ink)' }}
                  />
                  Perdidos
                </div>
                <div className="flex flex-1 items-center gap-1.5 px-2.5 py-2">
                  <span
                    className="inline-block h-2.5 w-2.5"
                    style={{ background: 'var(--nb-found)', border: '1.5px solid var(--nb-ink)' }}
                  />
                  Encontradas
                </div>
              </div>
            </NbCard>
          </section>
        </div>

        {/* Search by photo */}
        <section className="px-4 pt-5">
          <NbCard
            className="fill-ink cursor-pointer p-4 text-white"
            onClick={() => navigate('/search-by-photo')}
          >
            <div className="flex items-center gap-3">
              <div
                className="fill-lost flex h-12 w-12 shrink-0 items-center justify-center"
                style={{ border: '2.5px solid var(--nb-lost)' }}
              >
                <Camera size={22} />
              </div>
              <div className="flex-1">
                <div
                  className="font-display text-[20px] leading-none"
                  style={{ color: 'var(--nb-paper)' }}
                >
                  Buscar por
                  <br />
                  foto
                </div>
                <div
                  className="mt-1 text-[10px] uppercase tracking-[0.08em] opacity-70"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  match por color + forma
                </div>
              </div>
              <span className="text-xl" aria-hidden>
                →
              </span>
            </div>
          </NbCard>
        </section>

        {/* CTA categories */}
        <section className="px-4 pt-5">
          <div className="font-display mb-2.5 text-[22px]">Explorar</div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/lost')}
              className="nb fill-lost flex max-lg:aspect-square lg:min-h-[120px] cursor-pointer flex-col items-center justify-center p-3 text-center"
            >
              <div className="font-display break-words px-1 text-[18px] leading-none sm:text-[22px]">
                Perdidos
              </div>
              <div
                className="mt-1.5 text-[9px] font-bold uppercase tracking-[0.06em] sm:text-[10px]"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                alguien los busca
              </div>
            </button>
            <button
              onClick={() => navigate('/found')}
              className="nb fill-found flex max-lg:aspect-square lg:min-h-[120px] cursor-pointer flex-col items-center justify-center p-3 text-center"
            >
              <div className="font-display break-words px-1 text-[18px] leading-none sm:text-[22px]">
                Encontrados
              </div>
              <div
                className="mt-1.5 text-[9px] font-bold uppercase tracking-[0.06em] sm:text-[10px]"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                ¿los conocés?
              </div>
            </button>
          </div>
        </section>
      </div>

      {/* Counter band — full-bleed (sibling of centered container) */}
      <section className="mt-6">
        <div
          className="fill-solved"
          style={{
            borderTop: '3px solid var(--nb-ink)',
            borderBottom: '3px solid var(--nb-ink)',
          }}
        >
          <div className="mx-auto flex w-full max-w-md items-center gap-3.5 px-4 py-4 lg:max-w-[1280px] lg:justify-center lg:px-6">
            <div className="nb-seal shrink-0" style={{ borderWidth: 3 }}>
              <div>
                <div className="font-display text-[22px] leading-none">
                  {solvedCases?.solved_cases_count ?? '—'}
                </div>
                <div
                  className="text-[8px] font-bold"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  vueltas a casa
                </div>
              </div>
            </div>
            <div className="flex-1 lg:flex-initial">
              <div className="font-display text-[18px] leading-tight">
                {solvedCases?.solved_cases_count ?? '—'} reencuentros
                <br />
                hasta hoy.
              </div>
              <button
                onClick={() => navigate('/impacto')}
                className="mt-1.5 text-[12px] font-medium underline decoration-2 underline-offset-2"
              >
                Ver el impacto →
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
